import { Button, Input, Modal, Select, DatePicker, Pagination,
  notification, message, Tabs, Upload, Spin, Form, Switch, List, Checkbox
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { orderColumns } from './constants';
import { buildLeadingActionColumns, REFERENCE_LAYOUT_MARKER } from '../../utils/sharedTableColumns';
import { maxTagTooltip } from '../../utils/selectHelpers';
import './pacs.css';
import {
  getUserDetails,
  getDefaultCenterFilter,
  getVisibleCenterOptions,
  hisStatusOptions,
  hasReportingPermission,
  hasStudyViewingPermission,
  hasPermission,
  makeGetCall,
  makePostCall,
  parseMlcInfo,
  RADIOLOGY_URL,
} from '../../utils/helper';
import { DISABLE_DEFAULT_FILTERS } from '../../constants/APP_CONSTANTS';
import { useSites } from '../../hooks/useSites';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';
import { useBodyParts } from '../../hooks/useBodyParts';
import { useWebSocketContext } from '../../context/WebSocketContext';
import { useRegistrationTypes } from '../../hooks/useRegistrationTypes';
import { useModalityLocations } from '../../hooks/useModalityLocations';
import { SavedSearches } from '../orders/constants';
import { getAutoRefreshEnabled, subscribeAutoRefresh } from '../../utils/autoRefreshPubSub';
import { mergeShortcuts, shortcutToFilterAndRange, SHORTCUTS_UPDATED_EVENT } from '../../utils/shortcuts';

import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import { debounce, throttle } from 'lodash';
import TabPane from 'antd/es/tabs/TabPane';
import TextArea from 'antd/es/input/TextArea';
import ViewNotes from '../ManageNodes/ViewNotes';
import { useForm } from 'antd/es/form/Form';
import OrderInfoPopup from '../OrderInfo';
import PdfViewer from '../../components/PdfViewer';
import { launchViewerOnDualMonitor, checkMonitorSetup, openSeriesViewer, openReportingWindow } from '../../utils/MonitorManager';
import { SettingOutlined, UploadOutlined } from '@ant-design/icons';
import { downloadDocument, downloadDocumentPdf } from '../OnlyOfficeDocuments/documentsApi';
import {
  openReportWindow,
  showPdfInWindow,
  allowUnconfirmedReporting,
  filterReportableStudies,
  isCancelledStudy,
  withinReportLimit,
} from '../../utils/tableActionHandlers';
import PacsAgGrid from '../../components/PacsAgGrid';
import ColumnConfigurator from '../../components/PacsAgGrid/ColumnConfigurator';
import PatientOrdersModal, { PatientOrdersModalRef } from '../../components/PatientOrdersModal';
import { ColumnConfig, ColumnHeaderActions, PinnedColumnsConfig } from '../../components/PacsAgGrid/types';
import WhatsAppShareModal from '../../components/WhatsAppShareModal';
import DownloadStudyModal from '../../components/DownloadStudyModal';
import SeriesSelectionModal from '../../components/SeriesSelectionModal';
import EditStudyDetailsModal from '../../components/EditStudyDetailsModal';
import SplitStudyModal from '../../components/SplitStudyModal';
import StudyHistoryDialog from '../../components/StudyHistoryDialog';
import DicomUploadModal from '../../components/DicomUploadModal';
import AddFileModal from '../../components/AddFileModal';
import DeleteOrderModal from '../../components/DeleteOrderModal';
import AddToLibraryDialog from '../academics/AddToLibraryDialog';
import ExportStudyModal from '../../components/ExportStudyModal';
import AssignPanel from './AssignPanel';
import { FieldLabels, Permissions, getPacsStatusLabel } from '../../constants/fieldLabels';
import QuickDatePresets from '../../components/QuickDatePresets';
import {sortedModalityOptionsNew} from '../../utils/helper';
import { applyYhEntry } from '../../utils/yhSearch';

const { RangePicker } = DatePicker;

const PacsList = ({ StyledTabsComponent}) => {
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [saveFiltersModal, setSaveFiltersModal] = useState({ visible: false, data: {} });
  // HOD assign/unassign side-panel (old-PACS parity). refreshKey is bumped after
  // an assignment lands so the roster's Rep./Pend. counts re-fetch.
  const [assignPanelOpen, setAssignPanelOpen] = useState(false);
  const [rosterRefreshKey, setRosterRefreshKey] = useState(0);
  // Opening the panel collapses the tall filter fields to hand that vertical
  // space to the full-height roster + grid; closing it restores them. The
  // Show/Hide toggle in the toolbar can override this while the panel stays open.
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  useEffect(() => {
    setFiltersCollapsed(assignPanelOpen);
  }, [assignPanelOpen]);
  const [mlcModal, setMlcModal] = useState<{ visible: boolean; record: any }>({ visible: false, record: null });
  const [mlcSaving, setMlcSaving] = useState(false);
  const [mlcForm] = useForm();
  // Order deletion is gated behind a runtime env flag and limited to radiologists / HOD.
  const canDeleteOrder =
    !!(window as any).ENV_CONFIG?.ENABLE_ORDER_DELETE &&
    ['radiologist', 'hod'].includes(getUserDetails()?.user_type);
  const [deleteOrderModal, setDeleteOrderModal] = useState<{ visible: boolean; record: any }>({ visible: false, record: null });
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState<string | undefined>(undefined);
  const [userList, setUserList] = useState([]);
  const [viewNotesModal, setViewNotesModal] = useState({ visible: false, details: null });
  // Documentation Uploads (technician) — notes / file upload against the study's order.
  const [addFileModal, setAddFileModal] = useState<{ visible: boolean; data?: any }>({
    visible: false,
    data: null,
  });
  const [noteEditActive, setNoteEditActive] = useState(false);
  const [saveNoteHandler, setSaveNoteHandler] = useState(null);
  const [receiverModal, setReceiverModal] = useState({ visible: false, details: null });
  const [selectedNote, setSelectedNote] = useState({});
  const [orderInfoModal, setOrderInfoModal] = useState({});
  const [viewerModal, setViewerModal] = useState<{ visible: boolean; data: any; patient_id?: any; order_id?: any; doc_id?: number; patientData?: any }>({ visible: false, data: {} });
  const [pdfRefreshing, setPdfRefreshing] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(getAutoRefreshEnabled());

  const [filterName, setFilterName] = useState(null);
  const [filters, setFilters] = useState(() => ({ center: getDefaultCenterFilter() }));
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });
  const [isUserIdle, setIsUserIdle] = useState(false);

  // Use dynamic configuration hooks
  const { centers: allCenters, loading: centersLoading } = useSites();
  // Technicians only ever have (and may only pick) their own assigned center.
  const centers = useMemo(() => getVisibleCenterOptions(allCenters), [allCenters]);
  const { modalityOptions, loading: modalitiesLoading } = useAllowedModalities();
  const { bodyPartOptions, loading: bodyPartsLoading } = useBodyParts();
  const { regTypeOptions, loading: regTypesLoading } = useRegistrationTypes();
  // Location options are scoped to the selected Site/Group: each entry in the
  // modality_locations master carries an optional centre, and the ones without
  // a centre stay visible everywhere.
  const { locationOptions, loading: locationsLoading, error: locationsError } = useModalityLocations(filters['center']);
  const sortedModalityOptions = sortedModalityOptionsNew(modalityOptions);

  // When the site selection narrows (or a saved filter set restores a different
  // one), drop any already-selected location that no longer belongs to the
  // visible centres — otherwise the query keeps filtering on an option the
  // dropdown no longer offers.
  useEffect(() => {
    // Never prune off a failed fetch — an empty option list there means "we
    // don't know", not "nothing is valid".
    if (locationsLoading || locationsError) return;
    setFilters(prev => {
      const selected = prev['modality_location'];
      if (!Array.isArray(selected) || selected.length === 0) return prev;
      const valid = new Set(locationOptions.map(o => o.value));
      const pruned = selected.filter(v => valid.has(v));
      if (pruned.length === selected.length) return prev;
      return { ...prev, modality_location: pruned.length > 0 ? pruned : undefined };
    });
  }, [locationOptions, locationsLoading, locationsError]);

  // WebSocket context for real-time updates
  const wsContext = useWebSocketContext();

  const [savedFilters, setSavedFilters] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const userDetails = getUserDetails();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // Controlled open-state for the two searchable single-select filters so a
  // repeat click on the field toggles the dropdown closed (add-on only; antd
  // otherwise keeps a searchable select open on a second click).
  const [reportedByOpen, setReportedByOpen] = useState(false);
  const [assignedToOpen, setAssignedToOpen] = useState(false);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [printLoading, setPrintLoading] = React.useState(false);
  const [columnOrder, setColumnOrder] = useState([]);
  const [isLoadingColumnPreferences, setIsLoadingColumnPreferences] = useState(true);
  const [columnConfiguratorVisible, setColumnConfiguratorVisible] = useState(false);
  const [visibleColumnIds, setVisibleColumnIds] = useState<string[]>([]);
  const [pinnedColumns, setPinnedColumns] = useState<PinnedColumnsConfig>({ left: [], right: [] });
  const lastActivityRef = useRef(Date.now());

  const [receiverForm] = useForm();

  const today = dayjs();

  // Set the default value to [today at 00:00, today at 23:59] only if DISABLE_DEFAULT_FILTERS is false
  const [dateRange, setDateRange] = useState(DISABLE_DEFAULT_FILTERS ? null : [
    today.hour(0).minute(0),
    today.hour(23).minute(59)
  ]);

  const isHOD = userDetails?.user_type === 'hod';

  const userType = userDetails?.user_type;

  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedStudyForShare, setSelectedStudyForShare] = useState(null);

  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [selectedStudyForDownload, setSelectedStudyForDownload] = useState(null);

  const [editStudyModalVisible, setEditStudyModalVisible] = useState(false);
  const [selectedStudyForEdit, setSelectedStudyForEdit] = useState(null);

  const [splitStudyModalVisible, setSplitStudyModalVisible] = useState(false);
  const [selectedStudyForSplit, setSelectedStudyForSplit] = useState(null);

  // Radiant Viewer state - controlled by props and environment config
  // Only enable if both the prop is true AND the config flag is true
  const radiantAvailable = window.ENV_CONFIG?.RADIANT_AVAILABLE || false;
  const [enableRadiantViewer, setEnableRadiantViewer] = useState(!!radiantAvailable);
  const [studyHistoryModalVisible, setStudyHistoryModalVisible] = useState(false);
  const [selectedStudyForHistory, setSelectedStudyForHistory] = useState(null);

  const [uploadDicomModalVisible, setUploadDicomModalVisible] = useState(false);
  const [addToLibraryVisible, setAddToLibraryVisible] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [selectedStudyForExport, setSelectedStudyForExport] = useState(null);
  const patientOrdersModalRef = useRef<PatientOrdersModalRef>(null);

  const canAddToLibrary =
    (userDetails?.user_type === 'radiologist' || userDetails?.user_type === 'hod') &&
    !userDetails?.consultant;
  const selectedStudyRecords = useMemo(() => {
    if (!selectedRowKeys?.length || !orders?.data?.length) return [];
    const keySet = new Set(selectedRowKeys.map(k => String(k)));
    return orders.data.filter((r: any) =>
      keySet.has(String(r?.pacs_order?.pacs_ord_id))
    );
  }, [selectedRowKeys, orders?.data]);

  // Series selection modal state. The series fetch + table live in the shared
  // <SeriesSelectionModal>; this only tracks which study is open and the variant.
  const [seriesSelectionModal, setSeriesSelectionModal] = useState<{
    open: boolean;
    studyUID: string | null;
    record: any;
    infoOnly: boolean; // read-only technician "Series Info" variant (T009)
  }>({
    open: false,
    studyUID: null,
    record: null,
    infoOnly: false,
  });

  const handleShareStudy = record => {
    setSelectedStudyForShare(record);
    setShareModalVisible(true);
  };

  const handleDownloadStudy = record => {
    setSelectedStudyForDownload(record);
    setDownloadModalVisible(true);
  };

  const handleExportStudy = record => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }
    setSelectedStudyForExport(record);
    setExportModalVisible(true);
  };

  const handleEditStudyDetails = record => {
    setSelectedStudyForEdit(record);
    setEditStudyModalVisible(true);
  };

  const handleSplitStudy = record => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }
    setSelectedStudyForSplit(record);
    setSplitStudyModalVisible(true);
  };

  const openRadiantViewer = studyUID => {
    if (!studyUID) {
      message.error('Study UID not available');
      return;
    }

    // Open Radiant Viewer with the study UID
    const radiantUrl = `radiant://?n=pstv&v=0020000D&v=${studyUID}`;
    window.location.href = radiantUrl;
  };

  const handleShowStudyHistory = record => {
    setSelectedStudyForHistory(record);
    setStudyHistoryModalVisible(true);
  };

  const handleUploadDicom = () => {
    setUploadDicomModalVisible(true);
  };

  const handleShowPatientOrders = (record: any) => {
    patientOrdersModalRef.current?.open(record);
  };

  // Read-only series info dialog (T009 — technician). Opens the shared series
  // modal in its info-only variant (no checkboxes / no viewer launch), just
  // listing each series' description, modality and image count. No study-viewing
  // permission gate — it exposes metadata only.
  const handleSeriesInfo = (record: any) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }

    setSeriesSelectionModal({
      open: true,
      studyUID: record.ps_study_uid,
      record,
      infoOnly: true,
    });
  };

  // Handle series selection button click
  const handleSelectSeries = (record: any) => {
    if (!record?.ps_study_uid) {
      message.error('Study UID not found');
      return;
    }

    if (!hasStudyViewingPermission(userDetails)) {
      message.info('You do not have permission to view study images');
      return;
    }

    setSeriesSelectionModal({
      open: true,
      studyUID: record.ps_study_uid,
      record,
      infoOnly: false,
    });
  };

  const closeSeriesModal = () =>
    setSeriesSelectionModal(prev => ({ ...prev, open: false }));

  // Open viewer with the selected series. Routes through MonitorManager so the
  // study loads from its own centre's cache proxy (&dicomProxy=).
  const handleOpenSeries = async (
    studyUID: string,
    selectedSeries: string[],
    record: any
  ) => {
    openSeriesViewer(studyUID, selectedSeries, record);
  };

  useEffect(() => {
    // getPacsList();
    getSavedFilters();
    getUsersList();
    getQuickFilters();
    loadColumnPreferences();
  }, []);

  // Refresh chips when the user edits shortcuts from the profile dialog.
  useEffect(() => {
    const handler = () => getQuickFilters();
    window.addEventListener(SHORTCUTS_UPDATED_EVENT, handler);
    return () => window.removeEventListener(SHORTCUTS_UPDATED_EVENT, handler);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeAutoRefresh((enabled) => {
      setAutoRefreshEnabled(enabled);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Reset to page 1 when filters change
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
      setSelectedRowKeys([]); // Clear selection when filters change
    }
    debouncedFilter(filters);
  }, [filters]);


  // Pause auto-refresh while modals that depend on row selection are open.
  // Reason: filterResults() blanks orders.data briefly, which makes AG-Grid wipe
  // its internal selection and notify the parent — destroying the selection the
  // open dialog is operating on. See bookmark-plan.md / iter 1 notes.
  const refreshPaused = addToLibraryVisible;

  useEffect(() => {
    let intervalId;

    console.log('inside effect', autoRefreshEnabled, isUserIdle, refreshPaused);

    if (autoRefreshEnabled && !isUserIdle && !refreshPaused) {
      intervalId = setInterval(
        () => {
          // Only refresh if not currently loading and filters are applied
          if (!orders.loading) {
            console.log('Auto-refreshing studies list...');
            // Refresh current page only - maintains user position
            filterResults(filters, pagination.current, pagination.pageSize, true);
          }
        },
        (window.ENV_CONFIG?.AUTO_REFRESH_INTERVAL_MINUTES || 5) * 60 * 1000
      ); // 5 minutes
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefreshEnabled, isUserIdle, orders.loading, filters, dateRange, refreshPaused]);


  // Subscribe to WebSocket messages for real-time study updates
  useEffect(() => {
    const unsubscribe = wsContext.subscribe((data) => {
      try {

        // Handle study update notifications using state values directly
        if (data.type === 'study_update' && autoRefreshEnabled && !isUserIdle && !refreshPaused) {
          console.log('[PACS] Received WebSocket message:', data);
          // Only refresh if not currently loading
          if (!orders.loading) {
            console.log('Auto-refreshing studies list...');
            filterResults(filters, pagination.current, pagination.pageSize, true);
          }
        }
      } catch (e) {
        console.error('[PACS] Error in WS handler:', e);
      }
    });

    return () => unsubscribe();
  }, [wsContext, autoRefreshEnabled, isUserIdle, orders.loading, filters, pagination, refreshPaused]);



  // Refresh the list when the user returns to this browser tab.
  // Reporting opens in a SEPARATE tab (window.open('/oo-report', '_blank')), so
  // coming back from a signoff / active reporting session just refocuses this
  // still-mounted PACS tab — nothing re-fetches on its own. This surfaces the
  // updated reporting-status / status columns immediately on return.
  //
  // Gated on the Auto Sync toggle (getAutoRefreshEnabled) so that when auto-refresh is
  // OFF (the default), returning to the tab does NOT re-fetch — "off" means no auto refresh.
  // Read the live value at call time to avoid a stale closure. We still skip while a fetch
  // is in flight or a selection-dependent modal is open (refreshPaused).
  useEffect(() => {
    const debouncedRefocusRefresh = debounce(() => {
      if (!getAutoRefreshEnabled()) return;
      if (document.visibilityState !== 'visible') return;
      if (orders.loading || refreshPaused) return;
      console.log('Tab refocused — refreshing studies list...');
      filterResults(filters, pagination.current, pagination.pageSize, true);
    }, 400);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') debouncedRefocusRefresh();
    };
    const onFocus = () => debouncedRefocusRefresh();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      debouncedRefocusRefresh.cancel();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [filters, pagination, orders.loading, dateRange, refreshPaused]);


  useEffect(() => {
    const IDLE_TIME = 60 * 60 * 1000; // 60 minutes of inactivity

    const resetIdleTimer = () => {
      lastActivityRef.current = Date.now();
      if (isUserIdle) {
        setIsUserIdle(false);
      }
    };

    // Throttle the idle timer reset to prevent excessive function calls
    // Only update every 1 second instead of every mouse movement
    const throttledResetIdleTimer = throttle(resetIdleTimer, 1000, {
      leading: true,
      trailing: false,
    });

    const checkIdleStatus = () => {
      const now = Date.now();
      const timeSinceActivity = now - lastActivityRef.current;

      if (timeSinceActivity >= IDLE_TIME && !isUserIdle) {
        setIsUserIdle(true);
        console.log('User is idle, pausing auto-refresh');
      } else if (timeSinceActivity < IDLE_TIME && isUserIdle) {
        setIsUserIdle(false);
        console.log('User is active, resuming auto-refresh');
      }
    };

    // Events to track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, throttledResetIdleTimer, true);
    });

    // Check idle status every 30 seconds
    const idleCheckInterval = setInterval(checkIdleStatus, 30 * 1000);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledResetIdleTimer, true);
      });
      clearInterval(idleCheckInterval);
    };
  }, [isUserIdle]); // Removed lastActivity from dependencies!

  useEffect(() => {
    // if (Object.keys(filters).length) {
    debouncedFilter(filters);
    // }
  }, [dateRange]);

  const debouncedFilter = useCallback(
    debounce(tempFilters => {
      // When filters change via debounce, use page 1 (already set in useEffect)
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const getUsersList = () => {
    makePostCall('/user-list', {})
      .then(res => {
        setUserList(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setUserList([]);
      });
  };

  const getPacsList = async () => {
    makePostCall('/pacs-list', {
      role: userDetails?.user_type,
    })
      .then(res => {
        setOrders({ data: res.data?.data || [], loading: false });
      })
      .catch(e => {
        console.log(e);
        setOrders({ data: [], loading: false });
      });
  };

  const openViewer = async (record: any): Promise<void> => {
    if (hasStudyViewingPermission(userDetails)) {
      const viewerWindow = await launchViewerOnDualMonitor(record?.ps_study_uid, record);
      if (!viewerWindow) message.warning('Could not open viewer window');
    } else {
      message.info('You do not have permission to view study images');
    }
  };

  const openRadDesk = record => {
    if (hasStudyViewingPermission(userDetails)) {
      const { pacs_order } = record;
      const { patient, po_site } = pacs_order;
      const { pat_pin } = patient;
      window.open(`${RADIOLOGY_URL(pat_pin, po_site || '')}`, '_blank');
    } else {
      message.info('You do not habe the permission to view the study images');
    }
  };

  const openReport = async record => {
    // In DUAL_VIEWER_MODE, clicking the patient name opens viewers (not the report editor)
    if ((window as any).ENV_CONFIG?.DUAL_VIEWER_MODE) {
      return openViewer(record);
    }
    // Permission first — otherwise a user who cannot report at all would be asked to
    // acknowledge the unconfirmed warning before being told they cannot report.
    if (!hasReportingPermission(userDetails)) {
      message.info('You do not have the permission to do reporting');
      return;
    }
    // Studies marked "Not for reporting" cannot be opened for reporting.
    // Guards both entry points (patient-name click and the open-reporting action icon).
    if (record?.order_workflow?.ow_pacs_status === 'NOT_FOR_REPORTING') {
      message.info('This study is marked Not for reporting');
      return;
    }
    // Cancelled studies can never be opened for reporting. Mirrors the same guard in
    // tableActionHandlers.openReport (My Worklist) and the leading report-icon column,
    // so every entry point behaves identically.
    if (isCancelledStudy(record)) {
      message.info('This study is Cancelled and cannot be reported');
      return;
    }
    // Unconfirmed studies are blocked outright or merely warned about, depending on
    // the BLOCK_UNCONFIRMED_REPORTING flag.
    if (!(await allowUnconfirmedReporting(record))) return;
    const data = {
      order_ids: [record?.pacs_order?.pacs_ord_id].join(','),
    };
    // Convert data to URL parameters
    const params = new URLSearchParams(data).toString();
    // A tab here, or a window on the other monitor, per the user's workstation layout.
    await openReportingWindow(`/oo-report?${params}`);
  };

  const openSelectedStudies = async () => {
    if (hasReportingPermission(userDetails)) {
      // Drops "Not for reporting" studies, applies the unconfirmed rule (block or warn)
      // and handles all the skipped/abort messaging.
      const reportable = await filterReportableStudies(selectedStudyRecords, selectedRowKeys.length);
      if (!reportable) return;
      // Checked on what would actually open, not the raw selection: studies dropped
      // just above (not for reporting / cancelled / unconfirmed) must not count
      // against the user's cap.
      if (!withinReportLimit(reportable.length, userDetails)) return;
      const data = {
        order_ids: reportable.map(rec => rec?.pacs_order?.pacs_ord_id).join(','),
      };
      // Convert data to URL parameters
      const params = new URLSearchParams(data).toString();
      await openReportingWindow(`/oo-report?${params}`);
      // Clear the selection once the studies have been opened so the user
      // doesn't have to uncheck each row manually.
      setSelectedRowKeys([]);
    } else {
      message.info('You do not have the permission to do reporting');
    }
  };

  // Mark/unmark selected studies as "Not for reporting".
  // shouldMark=true marks SCANNED studies; shouldMark=false reverts
  // NOT_FOR_REPORTING studies back to SCANNED. Studies not in the eligible
  // source state are skipped (also enforced server-side).
  const toggleNotForReporting = (shouldMark: boolean) => {
    const fromStatus = shouldMark ? 'SCANNED' : 'NOT_FOR_REPORTING';
    const eligible = selectedStudyRecords.filter(
      rec => rec?.order_workflow?.ow_pacs_status === fromStatus
    );

    if (!eligible.length) {
      message.info(
        shouldMark
          ? 'Only SCANNED studies can be marked Not for reporting'
          : 'No Not-for-reporting studies selected'
      );
      return;
    }

    const skipped = selectedRowKeys.length - eligible.length;
    const payload = {
      order_ids: eligible.map(rec => rec?.pacs_order?.pacs_ord_id),
      user_id: getUserDetails()?.username,
      shouldMark,
    };

    makePostCall('/toggle-not-for-reporting', payload)
      .then(() => {
        message.success(
          shouldMark ? 'Marked as Not for reporting' : 'Marked as ready for reporting'
        );
        if (skipped > 0) {
          message.info(`${skipped} study(ies) were skipped`);
        }
        setSelectedRowKeys([]);
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log('Error while updating Not-for-reporting status', e);
        message.error(e.message);
      });
  };

  const getSavedFilters = async () => {
    makePostCall('/get-saved-filters', { user_id: getUserDetails()?.username })
      .then(res => {
        setSavedFilters(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setSavedFilters([]);
      });
  };

  const getQuickFilters = async () => {
    // Technicians don't use shortcuts on the PACS page — keep the chip row
    // hidden. (T006 fixed chips live on the Orders page only: PACS is
    // studies-based and isn't supposed to surface PENDING/NEW cases at all.)
    if (userType === 'technician') {
      setQuickFilters([]);
      return;
    }
    makePostCall('/get-my-shortcuts', { user_id: getUserDetails()?.username })
      .then(res => {
        setQuickFilters(mergeShortcuts(res.data?.data));
      })
      .catch(e => {
        console.log(e);
        setQuickFilters([]);
      });
  };

  const submitSaveFilters = () => {
    setSaveFiltersModal({ saveLoading: true });
    const payload = {
      filters: JSON.stringify(filters),
      uf_filter_name: filterName,
      user_id: getUserDetails()?.username,
    };
    makePostCall('/save-my-filters', payload)
      .then(res => {
        getSavedFilters();
        setSaveFiltersModal({ visible: false });
      })
      .catch(e => {
        console.log(e);
        message.error(e);
      });
  };

  const clearFilters = () => {
    setFilters({ center: getDefaultCenterFilter() });
    setDateRange(null);
    setModalityAllSelected(true);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    setSelectedRowKeys([]);
    // Don't call filterResults directly - let the useEffect on line 112-119 handle it via debouncedFilter
  };

  const yhSearchOptions = () => ({ scope: { center: getDefaultCenterFilter() } });
  const runSearch = () => {
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults(filters, 1, pagination.pageSize);
  };

  // `background = true` performs a silent, in-place refresh: the current rows stay
  // on screen and are swapped for the fresh response when it arrives, instead of
  // blanking to a loading spinner. Used by the tab-refocus / auto-refresh / WS
  // paths, where the filter set is unchanged and a flash-to-empty is jarring.
  // Explicit filter/page changes keep the spinner because the result set changes
  // meaning and showing stale rows would be misleading.
  const filterResults = (tempFilters, page = 1, pageSize = 20, background = false) => {
    if (background) {
      setOrders(prev => ({ ...prev, loading: false }));
    } else {
      setOrders({ ...orders, loading: true, data: [] });
    }
    const payload = {
      ...tempFilters,
      page: page - 1, // Backend expects 0-indexed page
      limit: pageSize,
    };

    if (dateRange) {
      payload['from_date'] = dayjs(dateRange[0]).format('YYYYMMDD');
      payload['to_date'] = dayjs(dateRange[1]).format('YYYYMMDD');
      payload['po_study_date'] = dateRange;
    }

    makePostCall('/pacs-list', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0,
        });
      })
      .catch(e => {
        console.log(e);
        // On a background refresh keep the existing rows rather than wiping them.
        if (background) {
          setOrders(prev => ({ ...prev, loading: false }));
        } else {
          setOrders({ data: [], loading: false, total: 0 });
        }
      });
  };

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    // Entering a YH No. (patient ID) clears the other filters once, so the search starts
    // from just that patient. Anything changed afterwards is kept and sent with it.
    const { entered, filters: effective } = applyYhEntry(filters, temp_filters, yhSearchOptions());
    if (entered) {
      setFilters(effective);
      setDateRange(null);
      setModalityAllSelected(true);
      return;
    }
    setFilters(temp_filters);
  };

  const statusOptions =
    userDetails?.user_type === 'doc'
      ? [{ label: 'SIGNEDOFF', value: 'SIGNEDOFF' }]
      : [
          // { label: 'PENDING', value: 'PENDING' },
          { label: getPacsStatusLabel('SCANNED'), value: 'SCANNED' },
          { label: 'DRAFTED', value: 'DRAFTED' },
          { label: 'REVIEWED', value: 'REVIEWED' },
          { label: 'SIGNEDOFF', value: 'SIGNEDOFF' },
          { label: 'NOT FOR REPORTING', value: 'NOT_FOR_REPORTING' },
          { label: 'CANCELLED', value: 'CANCELLED' },
        ];

  const handleFilterSelection = selectedSavedFilter => {
    const filterString = selectedSavedFilter.uf_filter_json;
    const filterJson = JSON.parse(filterString);

    setFilters(filterJson);
  };

  const handleQuickFilters = selectedFilter => {
    // Compound shortcut (master with qf_filter_json or user's own): apply the
    // whole filter set + date range at once.
    if (selectedFilter.filterRaw) {
      const { filters: f, dateRange: dr } = shortcutToFilterAndRange(selectedFilter.filterRaw);
      setFilters(f);
      // Keep the modality checkbox group in sync: a shortcut with a modality
      // subset should uncheck "all"; one without modality shows all.
      setModalityAllSelected(!(f.modality && f.modality.length));
      if (dr) setDateRange(dr);
      return;
    }

    // LEGACY (pre-compound shortcuts) — kept for context. Quick filters used to
    // be single-key rows: qf_type DATE set the date range, TOGGLE flipped a
    // boolean flag (Emergency/Re-reported), NA set one field=value. Migration
    // 20260618000003 converted all rows to compound qf_filter_json, so every
    // shortcut now takes the `filterRaw` path above and this branch is unused.
    //
    // const { qf_type, qf_date_start, qf_date_end, qf_key, qf_value } = selectedFilter;
    // if (qf_type === 'DATE') {
    //   // study_date
    //   const toDate = dayjs().subtract(qf_date_end, 'days').hour(23).minute(59).second(0);
    //   const fromDate = dayjs().subtract(qf_date_start, 'days').hour(0).minute(0).second(0);
    //   setDateRange([fromDate, toDate]);
    // } else if (qf_type === 'TOGGLE') {
    //   // Toggle the filter value (for Emergency, Re-reported, etc.)
    //   handleFilterChange(qf_key, !filters[qf_key]);
    // } else {
    //   setFilters({
    //     ...filters,
    //     [qf_key]: qf_value,
    //   });
    // }
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const showOrdDetails = rec => {
    setOrderInfoModal({ visible: true, data: rec });
  };

  const closeOrdInfo = () => {
    setOrderInfoModal({ visible: false, data: null });
  };

  // Bulk assign / unassign the selected studies. Pass `null` to unassign; omit
  // the argument to use the radiologist picked in the dialog. Signed-off cases
  // are locked, so the dialog blocks this action whenever any are selected —
  // meaning every id in `selectedRowKeys` here is guaranteed eligible.
  const assignToUser = (assignedTo?: string | null) => {
    const target = assignedTo === undefined ? selectedUsersToAssign : assignedTo;
    makePostCall('/assign-to-user', {
      order_ids: selectedRowKeys,
      assigned_to: target ?? null,
    })
      .then(res => {
        message.success(target ? 'Assigned successfully' : 'Unassigned successfully');
        setSelectedUsersToAssign(undefined);
        setSelectedRowKeys([]);
        // Re-fetch the roster so the side-panel's Rep./Pend. counts reflect the
        // assignment that just landed.
        setRosterRefreshKey(k => k + 1);
        // refresh the report data with current pagination
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log(e);
        message.error('Failed to update assignment');
      });
  };

  const captureAndPrint = () => {
    const receiverInfo = receiverForm.getFieldsValue();
    printReport(receiverModal?.details, receiverInfo);
  };

  const getDocId = (rec) => {
    const docId = rec?.oo_doc_id;
    if (!docId) {
      message.error('No document found for this report');
      return null;
    }
    return docId;
  };

  const buildPdfFilename = (rec) => {
    const { pacs_order } = rec;
    const { patient } = pacs_order;
    const sanitize = str => (str ? str.replace(/[^a-zA-Z0-9_]/g, '_').replace(/_+/g, '_') : '');
    const patientName = sanitize(patient?.pat_name || 'Unknown');
    const studyDesc = sanitize(pacs_order?.po_diag_desc || 'Study');
    const studyDate = pacs_order?.po_study_date ? dayjs(pacs_order.po_study_date).format('YYYYMMDD') : '';
    return `${patientName}_${studyDesc}_${studyDate}.pdf`;
  };

  const openPdfInWindow = (pdfBlob, filename) => {
    const url = URL.createObjectURL(pdfBlob);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${filename.replace('.pdf', '')}</title>
          <style>
            body { margin: 0; padding: 0; display: flex; flex-direction: column; height: 100vh; }
            .controls { padding: 10px; background: #f0f0f0; border-bottom: 1px solid #ccc; text-align: center; }
            .controls button { margin: 0 5px; padding: 8px 16px; cursor: pointer; background: #007bff; color: white; border: none; border-radius: 4px; font-size: 14px; }
            .controls button:hover { background: #0056b3; }
            embed { flex: 1; width: 100%; border: none; }
          </style>
        </head>
        <body>
          <div class="controls">
            <button onclick="downloadPDF()">Download PDF</button>
          </div>
          <embed src="${url}" type="application/pdf" />
          <script>
            function downloadPDF() {
              const a = document.createElement('a');
              a.href = '${url}';
              a.download = '${filename}';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
            }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const printReport = async (rec) => {
    const docId = getDocId(rec);
    if (!docId) return;

    setPrintLoading(true);
    try {
      const pdfBlob = await downloadDocumentPdf(docId);
      openPdfInWindow(pdfBlob, buildPdfFilename(rec));
    } catch (err) {
      console.error('Error printing report', err);
      message.error('Failed to generate PDF');
    } finally {
      setPrintLoading(false);
    }
  };

  const downloadDocxReport = async (rec) => {
    const docId = getDocId(rec);
    if (!docId) return;

    const { pacs_order } = rec;
    const filename = `report-${pacs_order.pacs_ord_id}-${pacs_order?.patient?.pat_name}.docx`;

    setPrintLoading(true);
    try {
      await downloadDocument(docId, filename);
      message.success('DOCX report downloaded successfully');
    } catch (err) {
      console.error('Error downloading DOCX report', err);
      message.error('Failed to download DOCX report');
    } finally {
      setPrintLoading(false);
    }
  };

  const viewReport = async (rec) => {
    const docId = getDocId(rec);
    if (!docId) return;

    const { pacs_order } = rec;
    const { pacs_ord_id, patient } = pacs_order;

    setPrintLoading(true);
    try {
      const pdfBlob = await downloadDocumentPdf(docId);
      const pdfBuffer = await pdfBlob.arrayBuffer();
      setViewerModal({
        visible: true,
        data: pdfBuffer,
        patient_id: patient?.pat_pacs_id,
        order_id: pacs_ord_id,
        doc_id: docId,
        patientData: rec,
      });
    } catch (err) {
      console.error('Error viewing report', err);
      message.error('Failed to load report');
      setViewerModal({ visible: false, data: null });
    } finally {
      setPrintLoading(false);
    }
  };

  // Status-column click opens the report in Chrome's native PDF viewer (a
  // separate popup window) instead of the in-app modal. Uses the same
  // OnlyOffice-rendered PDF source as `viewReport`.
  const openReportInWindow = async (rec) => {
    const docId = getDocId(rec);
    if (!docId) return;

    const reportWindow = openReportWindow();
    setPrintLoading(true);
    try {
      const pdfBlob = await downloadDocumentPdf(docId);
      showPdfInWindow(reportWindow, pdfBlob);
    } catch (err) {
      console.error('Error viewing report', err);
      message.error('Failed to load report');
      if (reportWindow && !reportWindow.closed) reportWindow.close();
    } finally {
      setPrintLoading(false);
    }
  };

  const refetchPdf = async () => {
    if (!viewerModal?.doc_id) return;

    setPdfRefreshing(true);
    try {
      const pdfBlob = await downloadDocumentPdf(viewerModal.doc_id);
      const pdfBuffer = await pdfBlob.arrayBuffer();
      setViewerModal(prev => ({ ...prev, data: pdfBuffer }));
      message.success('PDF refreshed successfully');
    } catch (err) {
      console.error('Error refreshing PDF', err);
      message.error('Failed to refresh PDF');
    } finally {
      setPdfRefreshing(false);
    }
  };

  const editSignedOffReport = rec => {
    const { pacs_order } = rec;
    const { pacs_ord_id } = pacs_order;
    openReportingWindow(`/oo-report?order_ids=${pacs_ord_id}`);
  };

  const viewNotes = rec => {
    const firstNote = rec.note_id || {};
    setSelectedNote(firstNote);
    setViewNotesModal({ visible: true, details: rec });
    setNoteEditActive(false);
  };

  // Documentation Uploads (technician) — same dialog as the Orders tab. The
  // upload is stored against the study's order (pacs_ord_id + patient pin), so a
  // study with no linked order has nothing to attach to.
  const addFile = rec => {
    const pacsOrder = rec?.pacs_order;
    if (!pacsOrder?.pacs_ord_id) {
      message.warning('No order linked to this study — nothing to attach the upload to');
      return;
    }
    setAddFileModal({ visible: true, data: { pacs_order: pacsOrder } });
  };

  const handleNoteSelection = useCallback((note, _record) => {
    setSelectedNote(prevSelectedNote => {
      const previousNoteId = (prevSelectedNote as any)?.rn_id || null;
      const nextNoteId = note?.rn_id || null;
      const previousNoteText = (prevSelectedNote as any)?.rn_notes || '';
      const nextNoteText = note?.rn_notes || '';

      if (previousNoteId === nextNoteId && previousNoteText === nextNoteText) {
        return prevSelectedNote;
      }

      return note || {};
    });
  }, []);

  const toggleReporting = (rec, idx) => {
    const { pacs_order } = rec;
    const { po_pacs_ord_id } = pacs_order;
    const shouldBlock = po_block_reporting !== 'Y';
    // toggle-reporting
    const payload = {
      user_id: getUserDetails()?.username,
      order_id: po_pacs_ord_id,
      shouldBlock,
    };
    makePostCall('/toggle-reporting', payload)
      .then(res => {
        message.success('Updated successfully');
        // Update by ID instead of index to support pagination
        const prevOrders = [...(orders?.data || [])];
        const recordIndex = prevOrders.findIndex(
          order => order.pacs_order?.pacs_ord_id === pacs_order?.pacs_ord_id
        );
        if (recordIndex !== -1) {
          prevOrders[recordIndex].po_block_reporting = shouldBlock ? 'Y' : 'N';
          setOrders({ ...orders, data: prevOrders, loading: false });
        }
      })
      .catch(e => {
        console.log('Error while blockig report', e);
        message.error(e.message);
      });
  };

  const toggleFeatures = (rec, idx, feature) => {
    const { pacs_order, order_workflow } = rec;
    const { pacs_ord_id, po_priority, po_his_status } = pacs_order;
    const { ow_reporting_blocked } = order_workflow;

    const shouldConfirm = po_his_status !== 'CONFIRMED';
    const currentlyBlocked = ow_reporting_blocked === true || ow_reporting_blocked === 'Y';
    const shouldBlock = !currentlyBlocked;
    const markEmergency = po_priority !== 'EMERGENCY';

    // toggle-reporting
    const payload = {
      user_id: getUserDetails()?.username,
      order_id: pacs_ord_id,
      // shouldConfirm,
    };

    let url = '';

    switch (feature) {
      case 'emergency':
        payload.markEmergency = markEmergency;
        url = '/toggle-emergency';
        objProperty = 'po_priority';
        objValue = markEmergency ? 'EMERGENCY' : 'NORMAL';
        break;
      case 'confirmation':
        payload.shouldConfirm = shouldConfirm;
        url = '/toggle-confirmation';
        objProperty = 'po_his_status';
        objValue = shouldConfirm ? 'CONFIRMED' : 'UNCONFIRMED';
        break;
      case 'reporting':
        payload.shouldBlock = shouldBlock;
        url = '/toggle-reporting';
        break;
      default:
        return;
    }

    makePostCall(url, payload)
      .then(res => {
        message.success('Updated successfully');
        // Refresh current page to reflect changes
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log('Error while blockig report', e);
        message.error(e.message);
      });
  };

  // Open the delete confirmation modal for an order soft-delete
  const openDeleteOrderModal = record => {
    setDeleteOrderModal({ visible: true, record });
  };

  // Soft-delete (cancel) the order; reason (optional) comes from the modal
  const submitDeleteOrder = reason => {
    const orderId = deleteOrderModal.record?.pacs_order?.pacs_ord_id;
    if (!orderId) return;
    setDeletingOrder(true);
    makePostCall('/delete-order', {
      order_id: orderId,
      user_id: getUserDetails()?.username,
      reason,
    })
      .then(res => {
        message.success('Order deleted');
        if (res.data?.dicom_error) {
          message.warning('Order hidden, but the study could not be deactivated in the DICOM server.');
        }
        setDeleteOrderModal({ visible: false, record: null });
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        message.error(e?.response?.data?.message || e.message || 'Failed to delete order');
      })
      .finally(() => setDeletingOrder(false));
  };

  // Open the MLC modal, pre-filling any existing MLC details for this study
  const markMlc = record => {
    const order = record?.pacs_order || {};
    const info = parseMlcInfo(order.po_mlc_info);
    mlcForm.setFieldsValue({
      mlc_number: order.po_mlc_number || '',
      category: info.category || undefined,
      station: info.station || '',
      fir: info.fir || '',
      remarks: info.remarks || '',
    });
    setMlcModal({ visible: true, record });
  };

  // Persist the MLC flag (mark with details, or un-mark). All fields optional.
  const submitMlc = shouldMark => {
    const order = mlcModal.record?.pacs_order;
    if (!order) return;
    const values = mlcForm.getFieldsValue();
    const payload = {
      order_id: order.pacs_ord_id,
      user_id: getUserDetails()?.username,
      markMlc: shouldMark,
      mlc_number: shouldMark ? (values?.mlc_number?.trim() || null) : null,
      mlc_info: shouldMark
        ? {
            category: values?.category || null,
            station: values?.station?.trim() || null,
            fir: values?.fir?.trim() || null,
            remarks: values?.remarks?.trim() || null,
          }
        : null,
    };
    setMlcSaving(true);
    makePostCall('/mark-mlc', payload)
      .then(() => {
        message.success(shouldMark ? 'Marked as MLC' : 'MLC marking removed');
        setMlcModal({ visible: false, record: null });
        mlcForm.resetFields();
        filterResults(filters, pagination.current, pagination.pageSize);
      })
      .catch(e => {
        console.log('Error while updating MLC', e);
        message.error(e.message);
      })
      .finally(() => setMlcSaving(false));
  };

  const toggleConfirmation = (rec, idx) => {
    const { pacs_order } = rec;
    const { pacs_ord_id, po_his_status } = pacs_order;
    const shouldConfirm = po_his_status !== 'CONFIRMED';
    // toggle-reporting
    const payload = {
      user_id: getUserDetails()?.username,
      order_id: pacs_ord_id,
      shouldConfirm,
    };
    makePostCall('/toggle-confirmation', payload)
      .then(res => {
        message.success('Updated successfully');
        // Update by ID instead of index to support pagination
        const prevOrders = [...(orders?.data || [])];
        const recordIndex = prevOrders.findIndex(
          order => order.pacs_order?.pacs_ord_id === pacs_order?.pacs_ord_id
        );
        if (recordIndex !== -1) {
          prevOrders[recordIndex].po_his_status = shouldConfirm ? 'CONFIRMED' : 'UNCONFIRMED';
          setOrders({ ...orders, data: prevOrders, loading: false });
        }
      })
      .catch(e => {
        console.log('Error while blockig report', e);
        message.error(e.message);
      });
  };

  // Persist a technician assignment (or unassignment) for an order and mirror
  // the result into local grid state. Passing a falsy `username` (e.g. the user
  // cleared the dropdown via the × button) means "unassign": we confirm first,
  // then POST user_id: null which the backend stores as NULL. The assign path
  // (non-empty username) is unchanged.
  const persistTechnicianAssignment = (rec, username: string | null) => {
    const { pacs_order } = rec;
    if (!pacs_order?.pacs_ord_id) return;
    const isUnassign = !username;
    makePostCall('/assign-to-tech', {
      user_id: username || null,
      order_id: pacs_order?.pacs_ord_id,
    })
      .then(() => {
        message.success(isUnassign ? 'Technician unassigned' : 'Technician assigned successfully');
        const prevOrders = [...(orders?.data || [])];
        const recordIndex = prevOrders.findIndex(
          order => order.pacs_order?.pacs_ord_id === pacs_order?.pacs_ord_id
        );
        if (recordIndex !== -1) {
          prevOrders[recordIndex].order_workflow.ow_assigned_technician = username || null;
          setOrders({ ...orders, data: prevOrders, loading: false });
        }
      })
      .catch(e => {
        console.log(isUnassign ? 'Error unassigning technician' : 'Error assigning technician', e);
        message.error(
          e?.response?.data?.message ||
            e.message ||
            (isUnassign ? 'Failed to unassign technician' : 'Failed to assign technician')
        );
      });
  };

  // NOTE: no longer reachable from this page — the Scan List technician column is
  // display-only and assignment happens on the Orders page. Kept (with its
  // plumbing below) so the column can be made interactive again if needed.
  const assignTechnician = (rec, username: string, _idx?: number) => {
    const { pacs_order } = rec;
    if (!pacs_order?.pacs_ord_id) return;
    // Empty value = the × was clicked → unassign, but confirm first so a stray
    // click can't silently remove a technician.
    if (!username) {
      const current = rec?.order_workflow?.ow_assigned_technician;
      if (!current) return; // nothing assigned — nothing to unassign
      Modal.confirm({
        title: 'Unassign this technician?',
        content: `This will remove ${current} from this study.`,
        okText: 'Unassign',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: () => persistTechnicianAssignment(rec, null),
      });
      return;
    }
    persistTechnicianAssignment(rec, username);
  };

  // Keep old name as alias so the orderColumns() calls in loadColumnPreferences / getDraggableColumns
  // that still reference assignToSelfTechnician continue to work.
  const assignToSelfTechnician = (rec, idx) => assignTechnician(rec, getUserDetails()?.username, idx);

  const handleRadiologistAssignment = (record, radiologistId, idx) => {
    const { pacs_order } = record;

    makePostCall('/assign-to-user', {
      order_ids: [pacs_order?.pacs_ord_id],
      assigned_to: radiologistId || null,
    })
      .then(res => {
        message.success(radiologistId ? 'Assigned successfully' : 'Unassigned successfully');

        // Update local state to reflect change immediately
        const prevOrders = [...(orders?.data || [])];
        const recordIndex = prevOrders.findIndex(
          order => order.pacs_order?.pacs_ord_id === pacs_order?.pacs_ord_id
        );

        if (recordIndex !== -1) {
          prevOrders[recordIndex].order_workflow.ow_assigned_rad_id = radiologistId || null;
          setOrders({ ...orders, data: prevOrders, loading: false });
        }
      })
      .catch(e => {
        console.log(e);
        message.error('Failed to update assignment');
      });
  };

  // Radiologists + HODs for the "Reported By" / "Assigned To" filters.
  // The username (user id) is appended after the name because two users can
  // share the same display name but have distinct user ids.
  const radHodOptions = useMemo(() => {
    return (userList || [])
      .filter(user => ['radiologist', 'hod'].includes(user.user_type))
      .map(user => ({
        label: `${user.user_fullname} (${user.username})`,
        value: user.username,
      }));
  }, [userList]);

  // Leading (left-pinned) action columns rendered before Patient Name for ALL
  // roles - mirroring the reference RIS layout where the action icons sit on the
  // left. Technicians get the trimmed three-icon set (Notes / Info / History);
  // everyone else gets the full action cluster (Notes / Report / Share / Series /
  // More). These live outside the configurable / draggable / saved-preference
  // column system via PacsAgGrid's `leadingColumns`.
  const leadingColumns = useMemo(
    () =>
      buildLeadingActionColumns({
        openViewer,
        showOrdDetails,
        openRadDesk,
        openReportEditor: openReport,
        viewNotes,
        addFile,
        printReport,
        downloadDocxReport,
        viewReport,
        editSignedOffReport,
        handleShareStudy,
        handleDownloadStudy,
        handleExportStudy,
        editStudyDetails: handleEditStudyDetails,
        showStudyHistory: handleShowStudyHistory,
        handleSelectSeries,
        handleSeriesInfo,
        splitStudy: handleSplitStudy,
        toggleReporting,
        toggleConfirmation,
        toggleFeatures,
        markMlc,
        deleteOrder: openDeleteOrderModal,
        canDeleteOrder,
        role: userDetails?.user_type,
        pageType: 'pacs',
      }),
    [userType, canDeleteOrder]
  );

  const FIXED_COLUMNS_COUNT = 2; // Pat ID and Patient Name are non-draggable

  const saveColumnPreferences = async (newColumnOrder, newPinnedColumns?: PinnedColumnsConfig) => {
    try {
      // Create a simplified version of column order (just store the keys/identifiers)
      // Pinning info is embedded per-column in the same JSON
      const columnKeys = newColumnOrder.map(col => {
        const colId = col.key || col.dataIndex || '';
        const identifier: any = {
          key: col.key || null,
          dataIndex: col.dataIndex || null,
          title: typeof col.title === 'string' ? col.title : null,
        };

        // If title is a React element, try to extract text
        if (typeof col.title !== 'string' && col.title?.props?.children) {
          if (typeof col.title.props.children === 'string') {
            identifier.title = col.title.props.children;
          } else if (Array.isArray(col.title.props.children)) {
            // Extract text from children array (skip icons)
            const textContent = col.title.props.children
              .filter(child => typeof child === 'string')
              .join('');
            identifier.title = textContent;
          }
        }

        // Embed pinning info per-column
        if (newPinnedColumns) {
          if (newPinnedColumns.left.includes(colId)) {
            identifier.pinned = 'left';
          } else if (newPinnedColumns.right.includes(colId)) {
            identifier.pinned = 'right';
          }
        }

        return identifier;
      });

      await makePostCall('/save-column-preferences', {
        user_id: getUserDetails()?.username,
        module: 'pacs_list',
        column_order: JSON.stringify(columnKeys),
      });

      console.log('Column preferences saved successfully');
    } catch (error) {
      console.error('Failed to save column preferences:', error);
    }
  };

  const loadColumnPreferences = async () => {
    try {
      setIsLoadingColumnPreferences(true);
      const response = await makeGetCall(
        `/get-column-preferences?user_id=${getUserDetails()?.username}&module=pacs-list`
      );

      // Get the base columns
      const baseColumns = orderColumns({
        openViewer: openViewer,
        openRadDesk,
        openReportEditor: openReport,
        role: userDetails?.user_type,
        assignToSelfTechnician,
        toggleReporting,
        viewNotes,
        printReport,
        downloadDocxReport,
        viewReport,
        editSignedOffReport,
        toggleConfirmation,
        toggleFeatures,
        markMlc,
        showOrdDetails,
        handleShareStudy,
        handleDownloadStudy,
        handleExportStudy,
        editStudyDetails: handleEditStudyDetails,
        showStudyHistory: handleShowStudyHistory,
        handleSelectSeries,
        splitStudy: handleSplitStudy,
        handleRadiologistAssignment,
        userList,
        openRadiantViewer,
        enableRadiantViewer: true,
        deleteOrder: openDeleteOrderModal,
        canDeleteOrder,
      });

      const savedColumnKeys = response.data?.data?.ucp_column_order
        ? JSON.parse(response.data.data.ucp_column_order)
        : null;

      // Discard saved preferences that predate the reference flex layout (they
      // lack the new Site column and carry the old fixed-width ordering). Once
      // the user re-customises, the saved set will include the marker and persist.
      const isStalePref =
        Array.isArray(savedColumnKeys) &&
        !savedColumnKeys.some(c => (c.key || c.dataIndex) === REFERENCE_LAYOUT_MARKER);

      if (savedColumnKeys && !isStalePref) {
        // Extract pinning info from per-column data
        const loadedPinned: PinnedColumnsConfig = { left: [], right: [] };
        savedColumnKeys.forEach(savedCol => {
          const colId = savedCol.key || savedCol.dataIndex;
          if (colId && savedCol.pinned === 'left') {
            loadedPinned.left.push(colId);
          } else if (colId && savedCol.pinned === 'right') {
            loadedPinned.right.push(colId);
          }
        });
        setPinnedColumns(loadedPinned);

        // Extract visible column IDs from the saved column order
        const savedVisibleIds = savedColumnKeys.map(col => col.key || col.dataIndex).filter(Boolean);
        setVisibleColumnIds(savedVisibleIds);

        // Rebuild column order by filtering baseColumns to only include visible IDs - ensuring columns not in savedVisibleIds are completely excluded
        const reorderedColumns: any[] = [];
        savedVisibleIds.forEach(colId => {
          const col = baseColumns.find(c => (c.key || c.dataIndex) === colId);
          if (col) {
            reorderedColumns.push(col);
          }
        });

        setColumnOrder(reorderedColumns);
        //sets the reordered clmns - only ones in visible column are shown
      } else {
        // No saved preferences (or stale pre-reference-layout prefs) - use the
        // default reference order. Actions are a leading cluster now, so nothing
        // is pinned right by default.
        setPinnedColumns({ left: [], right: [] });
        setColumnOrder([]);
      }
    } catch (error) {
      console.error('Failed to load column preferences:', error);
      // If loading fails, use defaults
      setPinnedColumns({ left: [], right: [] });
      setColumnOrder([]);
    } finally {
      setIsLoadingColumnPreferences(false);
    }
  };

  // Debounced save function to avoid too many API calls
  const debouncedSaveColumnPreferences = useCallback(
    debounce((columnOrder, currentPinnedColumns?: PinnedColumnsConfig) => {
      if (columnOrder.length > 0) {
        saveColumnPreferences(columnOrder, currentPinnedColumns);
      }
    }, 1000),
    []
  );
  // Add these drag and drop handler functions with useCallback
  const handleDragStart = useCallback((e, index) => {
    // Only allow dragging if the column is not in the fixed range
    if (index < FIXED_COLUMNS_COUNT) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.setData('text/plain', index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (e, dropIndex) => {
      e.preventDefault();

      // Don't allow dropping in the fixed columns area
      if (dropIndex < FIXED_COLUMNS_COUNT) {
        return;
      }

      const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));

      // Don't allow dragging fixed columns
      if (dragIndex < FIXED_COLUMNS_COUNT || dragIndex === dropIndex) {
        return;
      }

      // Use the current columnOrder state if it exists, otherwise get base columns
      const currentColumns =
        columnOrder.length > 0
          ? columnOrder
          : orderColumns({
              openViewer: openViewer,
              openRadDesk,
              openReportEditor: openReport,
              role: userDetails?.user_type,
              assignToSelfTechnician,
              openRadiantViewer,
              enableRadiantViewer,
              toggleReporting,
              viewNotes,
              printReport,
              downloadDocxReport,
              viewReport,
              editSignedOffReport,
              toggleConfirmation,
              toggleFeatures,
              markMlc,
              showOrdDetails,
              handleShareStudy,
              handleDownloadStudy,
              handleExportStudy,
              editStudyDetails: handleEditStudyDetails,
              showStudyHistory: handleShowStudyHistory,
              handleSelectSeries,
              splitStudy: handleSplitStudy,
              handleRadiologistAssignment,
              userList,
              deleteOrder: openDeleteOrderModal,
              canDeleteOrder,
            });

      const newColumns = [...currentColumns];
      const draggedColumn = newColumns[dragIndex];

      // Remove the dragged column
      newColumns.splice(dragIndex, 1);
      // Insert it at the new position
      newColumns.splice(dropIndex, 0, draggedColumn);

      setColumnOrder(newColumns);

      // Save the new column order to backend (debounced)
      debouncedSaveColumnPreferences(newColumns, pinnedColumns);
    },
    [columnOrder, pinnedColumns, userDetails?.user_type, debouncedSaveColumnPreferences]
  );

  // Header actions for PacsAgGrid columns (gear icon for column configurator).
  // The Actions column is now a leading (left-pinned) cluster outside the
  // configurable column system, so the gear lives on the Status ("Details")
  // header, which is present and visible for every role.
  const columnHeaderActions: ColumnHeaderActions = useMemo(() => {
    const gearColKey = 'status';
    return {
      [gearColKey]: {
        icon: <SettingOutlined />,
        onClick: () => setColumnConfiguratorVisible(true),
        tooltip: 'Configure Columns',
      },
    };
  }, []);

  // Create a memoized function to generate draggable columns
  const getDraggableColumns = useMemo(() => {
    const baseColumns =
      columnOrder.length > 0
        ? columnOrder
        : orderColumns({
            openViewer: openViewer,
            openRadDesk,
            openReportEditor: openReport,
            role: userDetails?.user_type,
            assignToSelfTechnician,
            toggleReporting,
            viewNotes,
            printReport,
            downloadDocxReport,
            viewReport,
            editSignedOffReport,
            toggleConfirmation,
            toggleFeatures,
            markMlc,
            showOrdDetails,
            handleShareStudy,
            handleDownloadStudy,
            handleExportStudy,
            editStudyDetails: handleEditStudyDetails,
            showStudyHistory: handleShowStudyHistory,
            handleSelectSeries,
            splitStudy: handleSplitStudy,
            handleUploadDicom,
            handleRadiologistAssignment,
            userList,
            openRadiantViewer,
            enableRadiantViewer,
            onShowPatientOrders: handleShowPatientOrders,
            deleteOrder: openDeleteOrderModal,
            canDeleteOrder,
          });

    return baseColumns.map((col, index) => {
      const colId = col.key || col.dataIndex;
      // Single source of truth: pinnedColumns state (driven by DB)
      let fixed: string | undefined;
      if (pinnedColumns.left.includes(colId)) {
        fixed = 'left';
      } else if (pinnedColumns.right.includes(colId)) {
        fixed = 'right';
      }

      return {
        ...col,
        fixed,
        title:
          index < FIXED_COLUMNS_COUNT ? (
            // Fixed columns - no drag handle, just the title
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                userSelect: 'none',
              }}
            >
              {col.title}
            </div>
          ) : (
            // Draggable columns - without visible drag handle
            <div
              className="draggable-header"
              draggable
              onDragStart={e => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={e => handleDrop(e, index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'move',
                userSelect: 'none',
              }}
            >
              {col.title}
            </div>
          ),
      };
    });
  }, [
    columnOrder,
    pinnedColumns,
    userDetails?.user_type,
    userList,
    handleRadiologistAssignment,
    openRadiantViewer,
    enableRadiantViewer,
  ]);

  // Get column configs for the configurator modal
  const allColumnConfigs: ColumnConfig[] = useMemo(() => {
    const baseColumns = orderColumns({
      openViewer: openViewer,
      openRadDesk,
      openReportEditor: openReport,
      role: userDetails?.user_type,
      assignToSelfTechnician,
      toggleReporting,
      viewNotes,
      printReport,
      downloadDocxReport,
      viewReport,
      editSignedOffReport,
      toggleConfirmation,
      toggleFeatures,
      markMlc,
      showOrdDetails,
      handleShareStudy,
      handleDownloadStudy,
      handleExportStudy,
      editStudyDetails: handleEditStudyDetails,
      showStudyHistory: handleShowStudyHistory,
      handleSelectSeries,
      splitStudy: handleSplitStudy,
      handleRadiologistAssignment,
      userList,
      openRadiantViewer,
      enableRadiantViewer,
    });

    return baseColumns.map(col => {
      // Extract header name from title (handles React elements)
      let headerName = '';
      if (typeof col.title === 'string') {
        headerName = col.title;
      } else if (col.title?.props?.children) {
        const children = col.title.props.children;
        if (typeof children === 'string') {
          headerName = children;
        } else if (Array.isArray(children)) {
          headerName = children.filter(c => typeof c === 'string').join('');
        }
      }

      return {
        colId: col.key || col.dataIndex || '',
        headerName: headerName || col.key || col.dataIndex || '',
      };
    });
  }, [userDetails?.user_type, userList]);

  // Handle column configuration save from the configurator modal
  const handleColumnConfigSave = useCallback((newVisibleColumnIds: string[], newPinnedColumns: PinnedColumnsConfig) => {
    setVisibleColumnIds(newVisibleColumnIds);
    setPinnedColumns(newPinnedColumns);

    // Rebuild column order based on visible column IDs
    const baseColumns = orderColumns({
      openViewer: openViewer,
      openRadDesk,
      openReportEditor: openReport,
      role: userDetails?.user_type,
      assignToSelfTechnician,
      toggleReporting,
      viewNotes,
      printReport,
      downloadDocxReport,
      viewReport,
      editSignedOffReport,
      toggleConfirmation,
      toggleFeatures,
      markMlc,
      showOrdDetails,
      handleShareStudy,
      handleDownloadStudy,
      handleExportStudy,
      editStudyDetails: handleEditStudyDetails,
      showStudyHistory: handleShowStudyHistory,
      handleSelectSeries,
      splitStudy: handleSplitStudy,
      handleRadiologistAssignment,
      userList,
      openRadiantViewer,
      enableRadiantViewer,
    });

    // Reorder columns based on visible column IDs
    const reorderedColumns: any[] = [];
    newVisibleColumnIds.forEach(colId => {
      const col = baseColumns.find(c => (c.key || c.dataIndex) === colId);
      if (col) {
        reorderedColumns.push(col);
      }
    });

    setColumnOrder(reorderedColumns);
    saveColumnPreferences(reorderedColumns, newPinnedColumns);
  }, [userDetails?.user_type, userList, openRadiantViewer, enableRadiantViewer]);

  // Extracted so the technician layout can place the filter fields inside the
  // shortcuts row (which SavedSearches leaves empty for technicians, T011) instead
  // of duplicating this markup across two JSX branches.
  const filterFieldsRow = (
    <div className="pacs-filters-row filter-layout-inputs">
      <div className="pacs-field pacs-field--name">
        <FyzksInput
          size="small"
          placeholder={FieldLabels.PATIENT_NAME}
          value={filters['pat_name'] || ''}
          onChange={e => handleFilterChange('pat_name', e.target.value)}
        />
      </div>

      <div className="pacs-field pacs-field--id">
        <FyzksInput
          size="small"
          placeholder={FieldLabels.PATIENT_ID}
          value={filters['po_pin'] || ''}
          onChange={e => handleFilterChange('po_pin', e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
        />
      </div>

      <div className="pacs-field pacs-field--acc">
        <FyzksInput
          size="small"
          placeholder={FieldLabels.ACCESSION_NUMBER_SHORTEST}
          value={filters['po_acc_no'] || ''}
          onChange={e => handleFilterChange('po_acc_no', e.target.value)}
        />
      </div>

      <div className="pacs-field pacs-field--visitid">
        <FyzksInput
          size="small"
          placeholder={FieldLabels.VISIT_ID}
          value={filters['po_visit_number'] || ''}
          onChange={e => handleFilterChange('po_visit_number', e.target.value)}
        />
      </div>

      <div className="pacs-field pacs-field--ordno">
        <FyzksInput
          size="small"
          placeholder={FieldLabels.ORDER_NUMBER}
          value={filters['his_ord_no'] || ''}
          onChange={e => handleFilterChange('his_ord_no', e.target.value)}
        />
      </div>

      <div className="pacs-field pacs-field--ref">
        <FyzksInput
          size="small"
          placeholder={FieldLabels.REFERRING_DOCTOR}
          value={filters['po_ref_doc'] || ''}
          onChange={e => handleFilterChange('po_ref_doc', e.target.value)}
        />
      </div>

      <div className="pacs-field pacs-field--body">
        <FyzksInput
          size="small"
          placeholder="Body Part / Study Desc"
          value={filters['po_body_part'] || ''}
          onChange={e => handleFilterChange('po_body_part', e.target.value)}
        />
      </div>

      <div className="pacs-field pacs-field--status">
        <Select
          size="small"
          mode="multiple"
          allowClear
          maxTagCount={1}
          className="single-line-tags"
          maxTagPlaceholder={maxTagTooltip}
          placeholder="PACS Status"
          value={filters['pacs_status']}
          style={{ width: '100%' }}
          options={statusOptions}
          onChange={val => handleFilterChange('pacs_status', val)}
        />
      </div>

      <div className="pacs-field pacs-field--center">
        <Select
          size="small"
          mode="multiple"
          allowClear
          maxTagCount={1}
          className="single-line-tags"
          maxTagPlaceholder={maxTagTooltip}
          placeholder="Site/Group"
          value={filters['center']}
          style={{ width: '100%' }}
          options={centers}
          onChange={val => handleFilterChange('center', val)}
          popupMatchSelectWidth={false}
          dropdownStyle={{ minWidth: '14rem' }}
          dropdownRender={menu => (
            <>
              <div
                className="select-all-dropdown-header"
                onMouseDown={e => e.preventDefault()}
              >
                <Checkbox
                  checked={centers.length > 0 && (filters['center']?.length || 0) === centers.length}
                  indeterminate={
                    (filters['center']?.length || 0) > 0 &&
                    (filters['center']?.length || 0) < centers.length
                  }
                  onChange={e =>
                    handleFilterChange('center', e.target.checked ? centers.map(c => c.value) : [])
                  }
                >
                  Select all
                </Checkbox>
              </div>
              {menu}
            </>
          )}
        />
      </div>

      {['radiologist', 'typist', 'hod', 'technician'].includes(userType) && (
        <div className="pacs-field pacs-field--location">
          <Select
            size="small"
            mode="multiple"
            allowClear
            placeholder="Location"
            value={filters['modality_location']}
            style={{ width: '100%' }}
            options={locationOptions}
            loading={locationsLoading}
            maxTagCount={1}
            className="single-line-tags"
            maxTagPlaceholder={maxTagTooltip}
            popupMatchSelectWidth={false}
            dropdownStyle={{ minWidth: '14rem' }}
            onChange={val => handleFilterChange('modality_location', val)}
          />
        </div>
      )}

      {['technician', 'radiologist'].includes(userType) && (
        <div className="pacs-field pacs-field--regtype">
          <Select
            size="small"
            allowClear
            placeholder="Registration Type"
            value={filters['po_pat_class']}
            style={{ width: '100%' }}
            options={regTypeOptions}
            loading={regTypesLoading}
            onChange={val => handleFilterChange('po_pat_class', val)}
          />
        </div>
      )}

      <div className="pacs-field pacs-field--hisstatus">
        <Select
          size="small"
          mode="multiple"
          allowClear
          maxTagCount={1}
          className="single-line-tags"
          maxTagPlaceholder={maxTagTooltip}
          placeholder="HIS Status"
          value={filters['po_his_status']}
          style={{ width: '100%' }}
          options={hisStatusOptions}
          onChange={val => handleFilterChange('po_his_status', val)}
        />
      </div>

      <div className="pacs-field pacs-field--reportedby">
        <Select
          size="small"
          showSearch
          mode="multiple"
          autoClearSearchValue={false}
          allowClear
          maxTagCount={1}
          className="single-line-tags"
          maxTagPlaceholder={maxTagTooltip}
          placeholder="Reported By"
          value={filters['po_reported_by']}
          style={{ width: '100%' }}
          popupMatchSelectWidth={false}
          dropdownStyle={{ minWidth: '16rem' }}
          options={radHodOptions}
          optionRender={(option) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox checked={filters['po_reported_by']?.includes(option.value)} style={{ pointerEvents: 'none' }} />
              <span>{option.label}</span>
            </div>
          )}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={val => handleFilterChange('po_reported_by', val)}
          // Add-on: clicking the field again while open closes the dropdown.
          // Handled on mousedown (fires before antd re-opens) and preventDefault
          // stops antd from re-focusing/re-opening the search box.
          open={reportedByOpen}
          onDropdownVisibleChange={setReportedByOpen}
          onMouseDown={e => {
            // React bubbles the dropdown (portal) events here too, so ignore
            // clicks on options — only toggle-close on a click on the field.
            if ((e.target as HTMLElement).closest('.ant-select-dropdown')) return;
            if (reportedByOpen) { e.preventDefault(); setReportedByOpen(false); }
          }}
        />
      </div>

      <div className="pacs-field pacs-field--assignedto">
        <Select
          size="small"
          showSearch
          allowClear
          mode="multiple"
          autoClearSearchValue={false}
          maxTagCount={1}
          className="single-line-tags"
          maxTagPlaceholder={maxTagTooltip}
          placeholder="Assigned To"
          value={filters['assigned_to']}
          style={{ width: '100%' }}
          popupMatchSelectWidth={false}
          dropdownStyle={{ minWidth: '16rem' }}
          options={radHodOptions}
          optionRender={(option) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Checkbox checked={filters['assigned_to']?.includes(option.value)} style={{ pointerEvents: 'none' }} />
              <span>{option.label}</span>
            </div>
          )}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          onChange={val => handleFilterChange('assigned_to', val)}
          // Add-on: clicking the field again while open closes the dropdown.
          // Handled on mousedown (fires before antd re-opens) and preventDefault
          // stops antd from re-focusing/re-opening the search box.
          open={assignedToOpen}
          onDropdownVisibleChange={setAssignedToOpen}
          onMouseDown={e => {
            // React bubbles the dropdown (portal) events here too, so ignore
            // clicks on options — only toggle-close on a click on the field.
            if ((e.target as HTMLElement).closest('.ant-select-dropdown')) return;
            if (assignedToOpen) { e.preventDefault(); setAssignedToOpen(false); }
          }}
        />
      </div>

      <div className="pacs-field pacs-field--date">
        <RangePicker
          size="small"
          placeholder={[FieldLabels.STUDY_DATE, '']}
          showTime={{
            format: 'HH:mm',
            use12Hours: false,
            defaultValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')],
          }}
          format="YYYY-MM-DD HH:mm"
          value={dateRange}
          onChange={val => {
            if (val) {
              setDateRange([val[0], val[1]]);
            } else {
              setDateRange(null);
            }
          }}
        />
      </div>
      <div className="pacs-field pacs-field--presets">
        <div className="date-with-presets">
          <QuickDatePresets value={dateRange} onChange={setDateRange} />
        </div>
      </div>
    </div>
  );

  const modalityGroup = (
    <div className={`filter-layout-modalities ${userType === 'technician' ? 'filter-layout-modalities--solo' : ''}`}>
      <div className="filter-modalities-header">
        <Checkbox
          className="filter-modalities-selectall"
          checked={modalityAllSelected}
          indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
          onChange={e => {
            setModalityAllSelected(e.target.checked);
            handleFilterChange('modality', []);
          }}
        >
          Select all
        </Checkbox>
      </div>
      <Checkbox.Group
        value={modalityAllSelected ? sortedModalityOptions.map(o => o.value) : (filters['modality'] || [])}
        onChange={vals => {
          const allChecked = vals.length === sortedModalityOptions.length;
          setModalityAllSelected(allChecked);
          handleFilterChange('modality', allChecked ? [] : vals);
        }}
      >
        {sortedModalityOptions.map(opt => (
          <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
        ))}
      </Checkbox.Group>
    </div>
  );

  // Re-reported filtering already exists via the bookmarks/shortcuts builder,
  // but users coming from the old PACS expect a plain checkbox right in the
  // filter section itself, so surface the same `show_rereported` filter here too.
  const reReportedCheckbox = (
    <div className="filter-rereported-row">
      <Checkbox
        checked={!!filters['show_rereported']}
        onChange={e => handleFilterChange('show_rereported', e.target.checked ? true : undefined)}
      >
        Show re-reported cases
      </Checkbox>
    </div>
  );

  const filterActionButtons = (
    <div className="filter-toolbar-actions">
      {selectedRowKeys.length > 0 ? (
        <>
          {userType === 'technician' &&
            hasPermission(Permissions.MARK_NOT_FOR_REPORTING) &&
            selectedStudyRecords.some(
              rec => rec?.order_workflow?.ow_pacs_status === 'SCANNED'
            ) && (
              <button
                className="btn-secondary btn-compact"
                onClick={() => toggleNotForReporting(true)}
              >
                Mark not for report
              </button>
            )}
          {userType === 'technician' &&
            hasPermission(Permissions.MARK_NOT_FOR_REPORTING) &&
            selectedStudyRecords.some(
              rec => rec?.order_workflow?.ow_pacs_status === 'NOT_FOR_REPORTING'
            ) && (
              <button
                className="btn-secondary btn-compact"
                onClick={() => toggleNotForReporting(false)}
              >
                Mark for report
              </button>
            )}
          {hasReportingPermission(userDetails) && (
            <button
              className="btn-primary btn-compact"
              onClick={() => openSelectedStudies()}
            >
              Open Studies
            </button>
          )}
          {canAddToLibrary && (
            <button
              className="btn-primary btn-compact"
              onClick={() => setAddToLibraryVisible(true)}
            >
              Add to Library
            </button>
          )}
          {selectedRowKeys.length === 1 && (
            <button
              className="btn-primary btn-compact"
              onClick={() => {
                setSelectedStudyForExport(selectedStudyRecords[0]);
                setExportModalVisible(true);
              }}
            >
              Push Study
            </button>
          )}
        </>
      ) : (
        <>
          <button
            className="btn-primary btn-compact"
            onClick={runSearch}
          >
            Search
          </button>
          <button
            className="btn-secondary btn-compact"
            onClick={() => { clearFilters(); }}
          >
            Clear Search
          </button>
          {/* <button
            className="btn-primary btn-compact"
            onClick={() => { setSaveFiltersModal({ visible: true }); }}
          >
            Save Filters
          </button> */}
          {hasPermission(Permissions.UPLOAD_DICOM) && (
            <button
              className="btn-primary btn-compact"
              onClick={() => handleUploadDicom()}
            >
              <UploadOutlined /> Upload DICOM
            </button>
          )}
        </>
      )}
      {/* HOD assign panel toggle — always available (not gated on a selection)
          so the roster can be opened first, old-PACS style. */}
      {isHOD && (
        <button
          className={`btn-compact ${assignPanelOpen ? 'btn-secondary' : 'btn-primary'}`}
          onClick={() => setAssignPanelOpen(o => !o)}
        >
          {assignPanelOpen ? 'Hide Assign Panel' : 'Assign / Unassign'}
        </button>
      )}
    </div>
  );

  return (
    <Spin spinning={printLoading}>
      <div>
        {/* Tab bar */}
        <div>{StyledTabsComponent && StyledTabsComponent()}</div>
        <div style={{ padding: '0 1.25rem' }}>
         <div className={`pacs-assign-shell${isHOD && assignPanelOpen ? ' is-open' : ''}`}>
          {isHOD && (
            <AssignPanel
              visible={assignPanelOpen}
              onClose={() => setAssignPanelOpen(false)}
              selectedStudyRecords={selectedStudyRecords}
              centers={filters['center']}
              onAssign={(username) => assignToUser(username)}
              onUnassign={() => assignToUser(null)}
              refreshKey={rosterRefreshKey}
            />
          )}
          <div className="pacs-assign-main">
          <div
            className={`pacs-filter-card${filtersCollapsed ? ' is-collapsed' : ''}`}
            style={{
              background: 'var(--pacs-surface, #FFFFFF)',
              borderRadius: '0.75rem',
              border: '1px solid var(--pacs-surface-border, #E2E8F1)',
              padding: '1rem 1.25rem',
              marginBottom: '1rem',
            }}
          >
            {/* Quick filters + action buttons share one row to save vertical space.
                Technicians get no shortcut chips (SavedSearches renders null), so the
                filter fields move into that row instead of leaving it blank (T011). */}
            {userType === 'technician' ? (
              <div className="filter-toolbar filter-toolbar--tech">
                <div className="filter-toolbar-main filter-toolbar-main--fields">
                  {filterFieldsRow}
                  {reReportedCheckbox}
                </div>
                <div className="filter-toolbar-side">
                  {filterActionButtons}
                  {modalityGroup}
                </div>
              </div>
            ) : (
              <>
                <div className="filter-toolbar">
                  <div className="filter-toolbar-main">
                    <SavedSearches
                      savedFilters={savedFilters || []}
                      handleFilterSelection={handleFilterSelection}
                      quickFilters={quickFilters || []}
                      handleQuickFilters={handleQuickFilters}
                      currentFilters={filters}
                    />
                  </div>
                  {isHOD && assignPanelOpen && (
                    <button
                      className="btn-secondary btn-compact pacs-filter-toggle"
                      onClick={() => setFiltersCollapsed(c => !c)}
                      title={filtersCollapsed ? 'Show the full filter fields' : 'Hide the filter fields to gain vertical space'}
                    >
                      {filtersCollapsed ? 'Show filters' : 'Hide filters'}
                    </button>
                  )}
                  {filterActionButtons}
                </div>
                {!filtersCollapsed && (
                  <>
                    <div className="filter-layout">
                      {filterFieldsRow}
                      {modalityGroup}
                    </div>
                    {reReportedCheckbox}
                  </>
                )}
              </>
            )}
            {/* QuickFilters component removed - now integrated into SavedSearches component above */}
          </div>
          <div className={`orders-list ${userType === 'technician' ? 'pacs-tech-dense' : ''}`}>
            <div className="pacs-grid-area">
            <PacsAgGrid
              height={filtersCollapsed ? 'calc(100vh - 14rem)' : 'calc(100vh - 20.7rem)'}
              rowData={orders.data || []}
              loading={orders.loading}
              columns={getDraggableColumns}
              leadingColumns={leadingColumns}
              fixedColumnsCount={FIXED_COLUMNS_COUNT}
              selectedRowKeys={selectedRowKeys}
              onSelectionChange={(keys) => setSelectedRowKeys(keys)}
              onOpenReport={openReport}
              onOpenViewer={openViewer}
              onViewReport={openReportInWindow}
              onOpenRadiant={openRadiantViewer}
              enableRadiantViewer={enableRadiantViewer}
              columnHeaderActions={columnHeaderActions}
              userList={userList}
              userType={userType}
              onRadiologistAssignment={handleRadiologistAssignment}
              onAssignToSelfTechnician={assignToSelfTechnician}
              onAssignTechnician={assignTechnician}
              currentUser={userDetails}
              onShowPatientOrders={['radiologist', 'technician', 'hod', 'typist'].includes(userType) ? handleShowPatientOrders : undefined}
              handleSelectSeries={handleSelectSeries}
              onColumnOrderChange={(newCols) => {
                // Save column order to backend (include current pinning)
                debouncedSaveColumnPreferences(newCols, pinnedColumns);
              }}
            />
            {/* External pagination for server-side paging */}
            <div className="pacs-ag-grid-pagination">
              <Pagination
                size="small"
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={orders?.total || 0}
                showSizeChanger
                pageSizeOptions={['10', '20', '50', '100']}
                onChange={(page, pageSize) => {
                  setPagination({ current: page, pageSize });
                  setSelectedRowKeys([]);
                  filterResults(filters, page, pageSize);
                }}
                showTotal={(total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`}
              />
            </div>
            </div>{/* /pacs-grid-area */}

            {/* Column Configurator Modal */}
            <ColumnConfigurator
              visible={columnConfiguratorVisible}
              onClose={() => setColumnConfiguratorVisible(false)}
              allColumns={allColumnConfigs}
              visibleColumnIds={visibleColumnIds}
              pinnedColumns={pinnedColumns}
              onSave={handleColumnConfigSave}
            />
            {saveFiltersModal.visible && (
              <Modal
                style={{
                  height: 'auto',
                }}
                styles={{
                  wrapper: {
                    transformOrigin: 'top center',
                  },
                }}
                className="save-filter-modal"
                onCancel={() => {
                  setSaveFiltersModal({ visible: false });
                }}
                okButtonProps={{ disabled: !filterName || saveFiltersModal.saveLoading }}
                onOk={submitSaveFilters}
                open={saveFiltersModal.visible}
              >
                <div className="pt-4">
                  <label>Enter a name for this filter set:</label>
                  <Input
                    placeholder="Type the name for the filters"
                    style={{ width: '18.75rem' }}
                    onChange={e => setFilterName(e.target.value)}
                  />
                </div>
              </Modal>
            )}

            {/* Documentation Uploads (technician action) */}
            {addFileModal && addFileModal.visible && (
              <AddFileModal
                open={addFileModal.visible}
                data={addFileModal.data}
                title="Documentation Uploads"
                initialType="upload"
                onClose={() => setAddFileModal({ visible: false, data: null })}
                onUploaded={() =>
                  filterResults(filters, pagination.current, pagination.pageSize, true)
                }
              />
            )}

            {viewNotesModal && viewNotesModal.visible && (
              <Modal
                open={viewNotesModal.visible}
                onCancel={() => {
                  if (noteEditActive) {
                    setNoteEditActive(false);
                    return;
                  }

                  setViewNotesModal(null);
                  setNoteEditActive(false);
                }}
                onOk={async () => {
                  if (saveNoteHandler) {
                    const saved = await saveNoteHandler();
                    if (!saved) {
                      return;
                    }
                  }
                }}
                style={{ width: '100%', height: '100%', top:'5%' }}
                width={'90%'}
                okButtonProps={{
                  style: {
                    marginTop: '20px',
                  },
                  disabled: !noteEditActive,
                }}
                okText="Save"
                cancelText={noteEditActive ? 'Cancel' : 'Close'}
              >
                <ViewNotes
                  handleNoteSelection={handleNoteSelection}
                  selectedNote={selectedNote}
                  viewNotesModal={viewNotesModal}
                  isEditMode={noteEditActive}
                  onEditNoteStateChange={setNoteEditActive}
                  onSaveNoteRegister={setSaveNoteHandler}
                />
              </Modal>
            )}
            {receiverModal && receiverModal.visible && (
              <Modal
                open={receiverModal.visible}
                onCancel={() => {
                  setReceiverModal(null);
                }}
                onOk={() => {
                  setReceiverModal(null);
                }}
                style={{ width: '100%', height: '100%' }}
                width={'90%'}
                okButtonProps={{ style: { display: 'none' } }}
              >
                <Form
                  form={receiverForm}
                  onFinish={() => {
                    captureAndPrint();
                  }}
                >
                  <Form.Item
                    name="receiver_name"
                    label="Receiver Name"
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="receiver_mobile"
                    label="Receiver Mobile"
                    rules={[
                      {
                        pattern: /^[6-9]\d{9}$/,
                        message: 'Please enter a valid 10-digit mobile number!',
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item>
                    <Button htmlType="submit">Submit</Button>
                    <Button type="link">Continue without Info</Button>
                  </Form.Item>
                </Form>
              </Modal>
            )}
            {orderInfoModal && orderInfoModal.visible && (
              <Modal
                // title="Patient & Order Information"
                open={orderInfoModal.visible}
                onCancel={closeOrdInfo}
                footer={[
                  <Button
                    key="cancel"
                    onClick={closeOrdInfo}
                  >
                    Cancel
                  </Button>,
                ]}
                width={'43.75rem'}
              >
                <OrderInfoPopup
                  data={orderInfoModal.data}
                  closeModal={closeOrdInfo}
                  refreshTable={() =>
                    filterResults(filters, pagination.current, pagination.pageSize)
                  }
                />
              </Modal>
            )}
            {viewerModal && (
              <Modal
                width={'62.5rem'}
                open={viewerModal?.visible}
                onCancel={() => {
                  setViewerModal({ visible: false });
                }}
                okButtonProps={{ style: { display: 'none' } }}
                style={{ top: '1.25rem' }}
                zIndex={1050}
                styles={{
                  body: {
                    height: '85vh',
                    maxHeight: '85vh',
                    overflow: 'hidden',
                    padding: 0,
                  },
                }}
              >
                <div style={{ height: '100%', width: '100%', overflow: 'auto' }}>
                  <PdfViewer
                    order_id={viewerModal?.order_id}
                    patient_id={viewerModal?.patient_id}
                    pdfArrayBuffer={viewerModal?.data}
                    patientData={viewerModal?.patientData}
                    onReportUpdated={refetchPdf}
                    refreshing={pdfRefreshing}
                    hideCopyButton={viewerModal?.patientData?.isFromPatientOrders}
                    hideWhatsappButton={viewerModal?.patientData?.isFromPatientOrders}
                    hideFullscreenButton
                  />
                </div>
              </Modal>
            )}

          </div>
          {selectedStudyForShare && (
            <WhatsAppShareModal
              visible={shareModalVisible}
              onCancel={() => {
                setShareModalVisible(false);
                setSelectedStudyForShare(null);
              }}
              studyUID={selectedStudyForShare?.ps_study_uid}
              studyDescription={selectedStudyForShare?.pacs_order?.po_diag_desc}
              pacOrdId={selectedStudyForShare?.pacs_order?.pacs_ord_id}
              patientName={selectedStudyForShare?.pacs_order?.patient?.pat_name}
              patientMobile={selectedStudyForShare?.pacs_order?.patient?.pat_mobile}
              patientGender={selectedStudyForShare?.pacs_order?.patient?.pat_sex}
              patientDOB={selectedStudyForShare?.pacs_order?.patient?.pat_dob}
              studyDate={selectedStudyForShare?.ps_study_dt_tm}
              refDoctorName={selectedStudyForShare?.pacs_order?.po_ref_doc}
              refDoctorContact={selectedStudyForShare?.pacs_order?.po_ref_doc_mobile}
            />
          )}

          {selectedStudyForDownload && (
            <DownloadStudyModal
              visible={downloadModalVisible}
              onCancel={() => {
                setDownloadModalVisible(false);
                setSelectedStudyForDownload(null);
              }}
              studyUID={selectedStudyForDownload?.ps_study_uid}
              studyDescription={selectedStudyForDownload?.pacs_order?.po_diag_desc}
              patientName={selectedStudyForDownload?.pacs_order?.patient?.pat_name}
              accessionNumber={selectedStudyForDownload?.pacs_order?.po_acc_no}
              studyData={selectedStudyForDownload}
            />
          )}

          {selectedStudyForEdit && (
            <EditStudyDetailsModal
              visible={editStudyModalVisible}
              onCancel={() => {
                setEditStudyModalVisible(false);
                setSelectedStudyForEdit(null);
              }}
              allowPatientIdEdit={userDetails?.user_type === 'technician'}
              onSuccess={() => {
                // Refresh the data after successful update
                filterResults(filters, pagination.current, pagination.pageSize);
              }}
              studyData={{
                patientName: selectedStudyForEdit?.pacs_order?.patient?.pat_name || '',
                patientId: selectedStudyForEdit?.pacs_order?.patient?.pat_pin || '',
                patientAge: selectedStudyForEdit?.pacs_order?.patient?.pat_age || '',
                patientDOB: selectedStudyForEdit?.pacs_order?.patient?.pat_dob,
                patientGender: selectedStudyForEdit?.pacs_order?.patient?.pat_sex || '',
                studyDescription: selectedStudyForEdit?.pacs_order?.po_diag_desc || '',
                accessionNumber: selectedStudyForEdit?.pacs_order?.po_acc_no || '',
                referringPhysician: selectedStudyForEdit?.pacs_order?.po_ref_doc || '',
                history: selectedStudyForEdit?.pacs_order?.po_history || '',
                bodyPart: selectedStudyForEdit?.pacs_order?.po_body_part || '',
                studyUID: selectedStudyForEdit?.ps_study_uid,
                orderId: selectedStudyForEdit?.pacs_order?.pacs_ord_id,
              }}
            />
          )}

          {selectedStudyForHistory && (
            <StudyHistoryDialog
              visible={studyHistoryModalVisible}
              orderId={selectedStudyForHistory?.pacs_order?.pacs_ord_id}
              onClose={() => {
                setStudyHistoryModalVisible(false);
                setSelectedStudyForHistory(null);
              }}
            />
          )}

          {selectedStudyForSplit && (
            <SplitStudyModal
              visible={splitStudyModalVisible}
              record={selectedStudyForSplit}
              onCancel={() => {
                setSplitStudyModalVisible(false);
                setSelectedStudyForSplit(null);
              }}
              onSuccess={() => {
                // Refresh so the newly-created split order appears in the list.
                filterResults(filters, pagination.current, pagination.pageSize);
              }}
            />
          )}

          {selectedStudyForExport && (
            <ExportStudyModal
              visible={exportModalVisible}
              studyUID={selectedStudyForExport?.ps_study_uid}
              studyModality={selectedStudyForExport?.pacs_order?.po_modality}
              onCancel={() => {
                setExportModalVisible(false);
                setSelectedStudyForExport(null);
                setSelectedRowKeys([]);
              }}
              onSuccess={() => {
                setSelectedRowKeys([]);
              }}
            />
          )}

          <DicomUploadModal
            visible={uploadDicomModalVisible}
            onCancel={() => {
              setUploadDicomModalVisible(false);
            }}
            onUploadSuccess={() => {
              // Optionally refresh the table or show success message
              message.success('DICOM files uploaded successfully');
            }}
          />

          <AddToLibraryDialog
            visible={addToLibraryVisible}
            studies={selectedStudyRecords}
            onClose={() => setAddToLibraryVisible(false)}
            onSaved={() => setSelectedRowKeys([])}
          />

          {/* Mark as MLC (Medico-Legal Case) Modal */}
          <Modal
            title="Mark as MLC (Medico-Legal Case)"
            open={mlcModal.visible}
            confirmLoading={mlcSaving}
            onCancel={() => {
              setMlcModal({ visible: false, record: null });
              mlcForm.resetFields();
            }}
            footer={[
              ...(mlcModal.record?.pacs_order?.po_is_mlc
                ? [
                    <Button key="unmark" danger loading={mlcSaving} onClick={() => submitMlc(false)}>
                      Unmark MLC
                    </Button>,
                  ]
                : []),
              <Button
                key="cancel"
                onClick={() => {
                  setMlcModal({ visible: false, record: null });
                  mlcForm.resetFields();
                }}
              >
                Cancel
              </Button>,
              <Button key="save" type="primary" loading={mlcSaving} onClick={() => submitMlc(true)}>
                {mlcModal.record?.pacs_order?.po_is_mlc ? 'Save' : 'Mark as MLC'}
              </Button>,
            ]}
          >
            <div style={{ marginBottom: 12, color: '#666' }}>
              Patient: <b>{mlcModal.record?.pacs_order?.patient?.pat_name || '-'}</b>
              {mlcModal.record?.pacs_order?.po_acc_no
                ? ` • Acc: ${mlcModal.record.pacs_order.po_acc_no}`
                : ''}
            </div>
            <Form form={mlcForm} layout="vertical">
              <Form.Item label="MLC Number" name="mlc_number">
                <Input placeholder="Optional — medico-legal / register number" allowClear />
              </Form.Item>
              <Form.Item label="Category / Type" name="category">
                <Select
                  allowClear
                  showSearch
                  placeholder="Optional — nature of the case"
                  options={[
                    { value: 'RTA', label: 'Road Traffic Accident (RTA)' },
                    { value: 'Assault', label: 'Assault' },
                    { value: 'Burns', label: 'Burns' },
                    { value: 'Poisoning', label: 'Poisoning' },
                    { value: 'Suicide Attempt', label: 'Suicide Attempt' },
                    { value: 'Sexual Assault', label: 'Sexual Assault' },
                    { value: 'Fall', label: 'Fall' },
                    { value: 'Other', label: 'Other' },
                  ]}
                />
              </Form.Item>
              <Form.Item label="Police Station" name="station">
                <Input placeholder="Optional — reporting police station" allowClear />
              </Form.Item>
              <Form.Item label="FIR Number" name="fir">
                <Input placeholder="Optional — FIR number" allowClear />
              </Form.Item>
              <Form.Item label="Remarks" name="remarks">
                <TextArea rows={3} placeholder="Optional — additional details" />
              </Form.Item>
            </Form>
          </Modal>

          {/* Delete Order confirmation + optional reason capture */}
          {deleteOrderModal.visible && (
            <DeleteOrderModal
              open={deleteOrderModal.visible}
              patientName={deleteOrderModal.record?.pacs_order?.patient?.pat_name}
              accessionNumber={deleteOrderModal.record?.pacs_order?.po_acc_no}
              loading={deletingOrder}
              onConfirm={submitDeleteOrder}
              onCancel={() => setDeleteOrderModal({ visible: false, record: null })}
            />
          )}
          {/* Patient Orders Popup */}
          <PatientOrdersModal
            ref={patientOrdersModalRef}
            onOpenReport={viewReport}
            onOpenViewer={openViewer}
            onViewReport={openReportInWindow}
            onOpenReporting={openReport}
          />
          {/* Series Selection / Info modal (shared with My Worklist). In
              infoOnly mode (technician, T009) it renders read-only. */}
          <SeriesSelectionModal
            open={seriesSelectionModal.open}
            studyUID={seriesSelectionModal.studyUID}
            record={seriesSelectionModal.record}
            infoOnly={seriesSelectionModal.infoOnly}
            onClose={closeSeriesModal}
            onOpenSeries={handleOpenSeries}
          />
          </div>{/* /pacs-assign-main */}
         </div>{/* /pacs-assign-shell */}
        </div>
      </div>
    </Spin>
  );
};

export default PacsList;
